import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function RsvpLanding() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [options, setOptions] = useState([]);

  const lookup = async (e) => {
    e.preventDefault();
    if (name.trim().length < 3) return toast.error("Please enter your name.");
    setLoading(true);
    setNotFound(false);
    setOptions([]);
    try {
      const { data } = await api.post("/rsvp/lookup", { name: name.trim() });
      if (data.found && data.token) {
        navigate(`/rsvp/${data.token}`);
      } else if (data.matches && data.matches.length > 0) {
        setOptions(data.matches);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-espresso text-ivory font-sans flex items-center justify-center px-6 py-16">
      <div className="max-w-lg w-full text-center" data-testid="rsvp-landing">
        <Link to="/" className="inline-flex items-center gap-2 text-gold/80 hover:text-gold text-sm mb-10"><ArrowLeft className="w-4 h-4" /> Angeo &amp; Gracie</Link>
        <p className="text-[11px] tracking-luxe uppercase text-gold">RSVP</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl text-ivory font-light">Find your invitation</h1>
        <p className="mt-5 text-cream/80 leading-relaxed">
          If you have your personal link, please open it to respond. Otherwise, enter the first or last name on your invitation below and we'll take you there.
        </p>

        <form onSubmit={lookup} className="mt-8">
          <div className="relative">
            <Search className="w-4 h-4 text-cream/40 absolute left-4 top-1/2 -translate-y-1/2" />
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name, last name, or full name" className="pl-11 h-12 bg-espresso-dark/60 border-gold-soft text-ivory placeholder:text-cream/40" data-testid="lookup-name" />
          </div>
          <button type="submit" disabled={loading} className="mt-4 w-full py-3.5 rounded-full bg-gold text-espresso-dark tracking-[0.14em] uppercase text-sm hover:bg-ivory transition-colors disabled:opacity-60" data-testid="lookup-submit">
            {loading ? "Searching..." : "Find My Invitation"}
          </button>
        </form>

        {options.length > 0 && (
          <div className="mt-8 text-left" data-testid="lookup-options">
            <p className="text-[11px] tracking-luxe uppercase text-gold mb-3 text-center">We found a few — please choose yours</p>
            <div className="space-y-2">
              {options.map((o) => (
                <button
                  key={o.token}
                  onClick={() => navigate(`/rsvp/${o.token}`)}
                  className="w-full flex items-center justify-between gap-3 rounded-sm border border-gold-soft bg-espresso-dark/60 px-5 py-4 hover:border-gold hover:bg-espresso-dark transition-colors group"
                  data-testid={`lookup-option-${o.token}`}
                >
                  <span className="font-display text-xl text-ivory">{o.invite_name}</span>
                  <span className="text-gold text-sm opacity-70 group-hover:opacity-100">Select</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {notFound && (
          <div className="mt-8 rounded-sm border border-gold-soft bg-espresso-dark/60 p-6" data-testid="lookup-not-found">
            <p className="text-cream/80">We couldn't find a match for that name. Please try the exact name on your invitation, or reach out and we'll help right away.</p>
            <a href="tel:9188220339" className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-gold text-gold text-sm hover:bg-gold hover:text-espresso-dark transition-colors">
              <Phone className="w-4 h-4" /> Call 918-822-0339
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
